import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { List, Row, Section } from './LegalPage';
import { LEGAL } from '../../config/legal';

/**
 * GDPR/UK GDPR and CCPA rights, plus the honest mechanics of exercising them.
 *
 * Deliberately says "email us and a human does it" rather than describing a
 * self-serve button, because no delete-account or export endpoint exists in the
 * backend yet. A rights page that promises a control the code does not have is
 * worse than one that promises a slower process it can actually honour.
 */
const DataRights: React.FC = () => (
  <LegalPage
    title="Your data rights"
    summary="What you can ask us to do with your data, how to ask, and how long we take."
  >
    <Section heading="How to make any request below">
      <p style={{ margin: 0 }}>
        Email <strong>{LEGAL.CONTACT_EMAIL}</strong> from the address on your account, and say which
        of the rights below you are exercising. We do not currently have self-serve buttons for
        these — a person handles each request by hand. We will confirm within a few days and complete
        it within <strong>30 days</strong>, which is the statutory deadline under GDPR. If a request
        is complex enough to need longer, we will tell you why before the 30 days are up.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        If the request comes from an address other than the one on the account, we will ask you to
        verify ownership first — otherwise anyone could ask for anyone's data.
      </p>
    </Section>

    <Section heading="Your rights under UK and EU GDPR">
      <div>
        <Row term="Access">
          Get a copy of the personal data we hold about you: your profile, your learning history and
          the prompts you have submitted.
        </Row>
        <Row term="Rectification">
          Correct anything wrong. Your name is editable in the app; email us for anything else.
        </Row>
        <Row term="Erasure">
          Have your account and its data deleted. Once done it cannot be undone — your progress,
          scores and history go with it.
        </Row>
        <Row term="Portability">
          Receive your data in a machine-readable format (JSON), or have it sent to another service
          where technically feasible.
        </Row>
        <Row term="Restriction">
          Ask us to keep your data but stop processing it while a dispute about it is resolved.
        </Row>
        <Row term="Objection">
          Object to processing we carry out on the basis of legitimate interests.
        </Row>
        <Row term="Withdraw consent">
          Where we rely on consent, withdraw it at any time. This does not affect processing that
          already happened.
        </Row>
        <Row term="Complain">
          Complain to your data protection authority — the ICO in the UK, or your national authority
          in the EEA. You do not have to come to us first, though we would rather you did so we can
          fix it.
        </Row>
      </div>
      <p style={{ margin: '14px 0 0' }}>
        Exercising these rights is free, and we will not treat you differently for it.
      </p>
    </Section>

    <Section heading="Why we are allowed to process your data (lawful bases)">
      <div>
        <Row term="Performing our contract">
          Running your account, saving your progress, and grading your prompts. Without this there is
          no service to provide.
        </Row>
        <Row term="Legitimate interests">
          Keeping the service secure, preventing abuse, and fixing faults — balanced against your
          rights, and limited to what that requires.
        </Row>
        <Row term="Legal obligation">
          Where the law requires us to keep or disclose something.
        </Row>
      </div>
      <p style={{ margin: '14px 0 0' }}>
        We do not process your data for advertising, profiling or automated decisions that produce
        legal effects. Challenge scoring is automated, but it only affects points and badges in a
        learning app — it does not decide anything about you in the real world.
      </p>
    </Section>

    <Section heading="If you are in California">
      <p style={{ margin: 0 }}>
        Under the CCPA as amended by the CPRA, you have the right to know what personal information we
        collect and why, to have it deleted, to correct it, and not to be discriminated against for
        asking. The categories we collect are set out in the{' '}
        <Link to="/privacy" style={{ color: 'var(--ember-strong)', fontWeight: 700, textDecoration: 'none' }}>Privacy Policy</Link>.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        <strong>We do not sell or share your personal information</strong>, as those terms are
        defined by the CCPA, and we never have. There is no "Do Not Sell or Share My Personal
        Information" flow because there is nothing to opt out of. We also do not process sensitive
        personal information for the purposes that would give you a right to limit it.
      </p>
    </Section>

    <Section heading="If you are in New York">
      <p style={{ margin: 0 }}>
        {LEGAL.SITE_NAME} is operated from New York, so New York's SHIELD Act applies: we are
        required to maintain reasonable safeguards for the private information of New York residents,
        and to notify you if a breach exposes it. New York does not currently have a comprehensive
        consumer privacy law giving residents the access and deletion rights that California's does.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        That does not change what you can ask for here: <strong>we extend every right listed above to
        anyone who asks, wherever you live.</strong> Sorting requests by jurisdiction would take more
        effort than simply honouring all of them.
      </p>
    </Section>

    <Section heading="What deletion actually removes">
      <List
        items={[
          'Your account record: name, email address and password hash.',
          'Your learning history: progress, points, level, streak, badges and quest state.',
          'The prompts you submitted and the scores against them.',
          'Your entry on the leaderboard.',
        ]}
      />
      <p style={{ margin: '10px 0 0' }}>
        Two honest caveats. Short-lived server logs may still contain a record that requests happened,
        including your email address, until they age out on their own rotation. And anything we are
        legally required to retain, we will retain — we will tell you if that applies to you, which
        for an ordinary account it will not.
      </p>
    </Section>

    <Section heading="Data protection contact">
      <p style={{ margin: 0 }}>
        Reach {LEGAL.ENTITY_NAME} at <strong>{LEGAL.CONTACT_EMAIL}</strong>. We are a small operation and
        are not required to appoint a Data Protection Officer, so this address reaches the person
        actually responsible rather than a ticket queue.
      </p>
    </Section>
  </LegalPage>
);

export default DataRights;
